import { useQueries } from "@tanstack/react-query";
import { WorkplaceCultureService } from "../../api/WorkplaceCultureService";
import {
  DemographicGroupWithDetails,
  GetSurveyChapterScoringResponse,
  GetSurveyScoringDemographicResponse,
} from "../../types/workplaceCulture";

interface DemographicScore {
  scores: Record<string, number>;
  chapterScores: Record<
    string,
    Array<{ id: string; name: string; score: number }>
  >;
  isLoading: boolean;
}

export const useCustomDemographicScores = (
  customDemographicGroups: DemographicGroupWithDetails[],
  dispatchToken: string
): DemographicScore => {
  const workplaceCultureService = new WorkplaceCultureService();

  // Collect all demographic IDs
  const demographicIds = customDemographicGroups.flatMap((group) =>
    group.demographics.map((demographic) => demographic.id)
  );

  // Fetch scores for all demographics
  const scoreQueries = useQueries({
    queries: demographicIds.map((id) => ({
      queryKey: [`customDemographicScore-${dispatchToken}-${id}`],
      queryFn: () =>
        workplaceCultureService.getSurveyScoring(dispatchToken, id),
      enabled: !!dispatchToken && !!id,
    })),
  });

  // Fetch chapter scores for all demographics
  const chapterScoreQueries = useQueries({
    queries: demographicIds.map((id) => ({
      queryKey: [`customDemographicChapterScore-${dispatchToken}-${id}`],
      queryFn: () =>
        workplaceCultureService.getSurveyChapterScoring(dispatchToken, id),
      enabled: !!dispatchToken && !!id,
    })),
  });

  // Extract scores from query results
  const scores: Record<string, number> = {};
  scoreQueries.forEach((query, index) => {
    const demographicId = demographicIds[index];
    const data = query.data as GetSurveyScoringDemographicResponse | undefined;
    scores[demographicId] = data?.scores.total_score || 0;
  });

  // Extract chapter scores from query results
  const chapterScores: Record<
    string,
    Array<{ id: string; name: string; score: number }>
  > = {};
  chapterScoreQueries.forEach((query, index) => {
    const demographicId = demographicIds[index];
    const data = query.data as GetSurveyChapterScoringResponse | undefined;
    chapterScores[demographicId] =
      data?.chapters.map((chapter) => ({
        id: chapter.id,
        name: chapter.name,
        score: chapter.demographic.scores.total_score || 0,
      })) || [];
  });

  // Check if any queries are still loading
  const isLoading =
    scoreQueries.some((query) => query.isLoading) ||
    chapterScoreQueries.some((query) => query.isLoading);

  return {
    scores,
    chapterScores,
    isLoading,
  };
};
